import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '@/components/layouts/AdminLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import {
  BookOpen, Plus, Search, Edit, Trash2, Eye, EyeOff, ArrowLeft,
  Loader2, Tag, BarChart3
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const KB_CATEGORIES = [
  { value: 'getting_started', label: 'Getting Started' },
  { value: 'payments', label: 'Payments & Transactions' },
  { value: 'api', label: 'API & Integration' },
  { value: 'billing', label: 'Billing & Plans' },
  { value: 'security', label: 'Security & Account' },
  { value: 'troubleshooting', label: 'Troubleshooting' },
];

const CAT_COLORS = {
  getting_started: 'bg-emerald-500/15 text-emerald-400',
  payments: 'bg-indigo-500/15 text-indigo-400',
  api: 'bg-cyan-500/15 text-cyan-400',
  billing: 'bg-amber-500/15 text-amber-400',
  security: 'bg-red-500/15 text-red-400',
  troubleshooting: 'bg-purple-500/15 text-purple-400',
};

const ArticleEditor = ({ article, onSave, onCancel }) => {
  const [form, setForm] = useState({
    title: article?.title || '',
    category: article?.category || 'getting_started',
    content: article?.content || '',
    tags: (article?.tags || []).join(', '),
    published: article?.published !== false,
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!form.title.trim() || !form.content.trim()) { toast.error('Title and content required'); return; }
    setSaving(true);
    try {
      const payload = {
        title: form.title,
        category: form.category,
        content: form.content,
        tags: form.tags.split(',').map(t => t.trim()).filter(Boolean),
        published: form.published,
      };
      if (article?.id) {
        await axios.put(`${API}/kb/admin/articles/${article.id}`, payload);
        toast.success('Article updated');
      } else {
        await axios.post(`${API}/kb/admin/articles`, payload);
        toast.success('Article created');
      }
      onSave();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setSaving(false);
  };

  return (
    <div className="space-y-4" data-testid="kb-editor">
      <Button variant="ghost" size="sm" onClick={onCancel}><ArrowLeft className="h-4 w-4 mr-1" /> Back</Button>
      <Card className="border-border bg-card">
        <CardContent className="py-5 space-y-4">
          <h2 className="text-base font-semibold text-foreground">{article?.id ? 'Edit Article' : 'New Article'}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Title</label>
              <Input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
                placeholder="Article title" data-testid="kb-title" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Category</label>
              <select className="w-full h-9 rounded-md border border-border bg-background px-3 text-sm text-foreground"
                value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))} data-testid="kb-category">
                {KB_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Content (Markdown supported)</label>
            <Textarea rows={16} value={form.content} onChange={e => setForm(p => ({ ...p, content: e.target.value }))}
              placeholder="Write your article content here..." className="font-mono text-xs" data-testid="kb-content" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Tags (comma-separated)</label>
              <Input value={form.tags} onChange={e => setForm(p => ({ ...p, tags: e.target.value }))}
                placeholder="setup, onboarding, beginner" data-testid="kb-tags" />
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.published} onChange={e => setForm(p => ({ ...p, published: e.target.checked }))} data-testid="kb-published" />
                <span className="text-xs text-foreground">Published</span>
              </label>
            </div>
          </div>
          <div className="flex gap-2 pt-2">
            <Button size="sm" onClick={handleSave} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="kb-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
              {article?.id ? 'Update' : 'Create'} Article
            </Button>
            <Button size="sm" variant="outline" onClick={onCancel}>Cancel</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const AdminKnowledgeBase = () => {
  const [articles, setArticles] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null = list, {} = new, {id:...} = edit
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('');

  const fetchArticles = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (activeCategory) params.category = activeCategory;
      if (search) params.search = search;
      const { data } = await axios.get(`${API}/kb/admin/articles`, { params });
      setArticles(data.data || []);
    } catch {}
    setLoading(false);
  }, [activeCategory, search]);

  const fetchStats = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/kb/admin/stats`); setStats(data); } catch {}
  }, []);

  useEffect(() => { fetchArticles(); fetchStats(); }, [fetchArticles, fetchStats]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this article?')) return;
    try { await axios.delete(`${API}/kb/admin/articles/${id}`); toast.success('Deleted'); fetchArticles(); fetchStats(); } catch { toast.error('Failed'); }
  };

  if (editing !== null) {
    return (
      <AdminLayout title="Knowledge Base">
        <div className="p-6 max-w-3xl mx-auto">
          <ArticleEditor article={editing?.id ? editing : null}
            onSave={() => { setEditing(null); fetchArticles(); fetchStats(); }}
            onCancel={() => setEditing(null)} />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Knowledge Base">
      <div className="p-6 space-y-5" data-testid="admin-kb-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Knowledge Base</h1>
            <p className="text-sm text-muted-foreground mt-0.5">Manage help articles for merchants</p>
          </div>
          <Button size="sm" onClick={() => setEditing({})} className="bg-indigo-600 hover:bg-indigo-700" data-testid="kb-new-article">
            <Plus className="h-4 w-4 mr-1" /> New Article
          </Button>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card className="border-border bg-card"><CardContent className="py-3 text-center">
              <p className="text-2xl font-bold text-foreground">{stats.total}</p>
              <p className="text-[10px] text-muted-foreground">Total Articles</p>
            </CardContent></Card>
            <Card className="border-border bg-card"><CardContent className="py-3 text-center">
              <p className="text-2xl font-bold text-emerald-400">{stats.published}</p>
              <p className="text-[10px] text-muted-foreground">Published</p>
            </CardContent></Card>
            <Card className="border-border bg-card"><CardContent className="py-3 text-center">
              <p className="text-2xl font-bold text-amber-400">{stats.draft}</p>
              <p className="text-[10px] text-muted-foreground">Drafts</p>
            </CardContent></Card>
            <Card className="border-border bg-card"><CardContent className="py-3 text-center">
              <p className="text-2xl font-bold text-indigo-400">{Object.keys(stats.by_category || {}).length}</p>
              <p className="text-[10px] text-muted-foreground">Categories Used</p>
            </CardContent></Card>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-1.5">
            <Search className="h-4 w-4 text-muted-foreground" />
            <Input placeholder="Search articles..." value={search} className="h-8 w-64 text-xs"
              onChange={e => setSearch(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') fetchArticles(); }} data-testid="admin-kb-search" />
          </div>
          <div className="flex gap-1.5 flex-wrap">
            <button className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${!activeCategory ? 'bg-indigo-600 text-white' : 'bg-muted/30 text-muted-foreground hover:text-foreground'}`}
              onClick={() => setActiveCategory('')}>All</button>
            {KB_CATEGORIES.map(c => (
              <button key={c.value} className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${activeCategory === c.value ? 'bg-indigo-600 text-white' : 'bg-muted/30 text-muted-foreground hover:text-foreground'}`}
                onClick={() => setActiveCategory(c.value)}>{c.label}</button>
            ))}
          </div>
        </div>

        {/* Article List */}
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
        ) : articles.length === 0 ? (
          <Card className="border-border bg-card"><CardContent className="py-12 text-center text-muted-foreground">
            <BookOpen className="h-10 w-10 mx-auto mb-3 opacity-50" />
            <p>No articles found</p>
            <Button size="sm" variant="outline" className="mt-3" onClick={() => setEditing({})}>Create your first article</Button>
          </CardContent></Card>
        ) : (
          <div className="space-y-2">
            {articles.map(a => {
              const cc = CAT_COLORS[a.category] || 'bg-muted/30 text-muted-foreground';
              return (
                <Card key={a.id} className="border-border bg-card" data-testid={`admin-kb-article-${a.id}`}>
                  <CardContent className="py-3">
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`px-2 py-0.5 rounded text-[10px] font-semibold ${cc}`}>{a.category?.replace('_', ' ')}</span>
                          {!a.published && <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-amber-500/15 text-amber-400">Draft</span>}
                          <span className="text-[10px] text-muted-foreground flex items-center gap-0.5"><Eye className="h-3 w-3" /> {a.views || 0}</span>
                        </div>
                        <h3 className="text-sm font-semibold text-foreground">{a.title}</h3>
                        {a.tags?.length > 0 && (
                          <div className="flex items-center gap-1 mt-1">
                            <Tag className="h-3 w-3 text-muted-foreground" />
                            {a.tags.map(t => <span key={t} className="text-[10px] text-muted-foreground">{t}</span>)}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <Button size="sm" variant="ghost" onClick={() => setEditing(a)} data-testid={`edit-${a.id}`}>
                          <Edit className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-300" onClick={() => handleDelete(a.id)} data-testid={`delete-${a.id}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminKnowledgeBase;
